import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Search, Plus, Pencil, Trash2, ChevronDown, ChevronRight, Loader2,
    GripVertical, Building2, Sparkles, Languages, X, CheckSquare, Square,
    SlidersHorizontal, Eye, EyeOff, Tag,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
    Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import CategoryEditPanel from "@/components/categories/CategoryEditPanel";
import { categoriesApi } from "@/lib/api/categories";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { ROLES } from "@/lib/roles";
import { formatApiErrorDetail } from "@/lib/api";

/* ------------------------------------------------------------------ */
/*  Helpers                                                           */
/* ------------------------------------------------------------------ */

function useDebounce(value, delay = 250) {
    const [v, setV] = useState(value);
    useEffect(() => { const t = setTimeout(() => setV(value), delay); return () => clearTimeout(t); }, [value, delay]);
    return v;
}

function StatusDot({ active }) {
    return <span className={`inline-block h-2 w-2 rounded-full shrink-0 ${active ? "bg-success" : "bg-muted-foreground/40"}`} />;
}

function fmtDate(iso, lang) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString(lang === "ar" ? "ar-EG" : "en-GB",
            { day: "2-digit", month: "short" });
    } catch (_) { return iso; }
}

const EXPANDED_KEY = "admin_categories_expanded_v2";

function loadExpandedState() {
    try {
        const raw = localStorage.getItem(EXPANDED_KEY);
        if (!raw) return new Set();
        return new Set(JSON.parse(raw));
    } catch { return new Set(); }
}

function saveExpandedState(set) {
    try { localStorage.setItem(EXPANDED_KEY, JSON.stringify(Array.from(set))); } catch { /* ignore */ }
}

/* ------------------------------------------------------------------ */
/*  Inline rename                                                     */
/* ------------------------------------------------------------------ */

function InlineName({ value, lang, isL1, dir = "ltr", onCommit }) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(value);

    useEffect(() => { setDraft(value); }, [value]);

    if (!editing) {
        return (
            <span
                className={`${isL1 ? "font-semibold" : ""} cursor-text hover:bg-accent/50 px-0.5 rounded`}
                onDoubleClick={(e) => { e.stopPropagation(); setEditing(true); }}
                title="Double-click to rename"
            >
                {value || <span className="italic text-muted-foreground">—</span>}
            </span>
        );
    }

    const commit = async () => {
        const next = draft.trim();
        setEditing(false);
        if (!next || next === value) { setDraft(value); return; }
        try {
            await onCommit(next);
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
            setDraft(value);
        }
    };

    return (
        <Input
            autoFocus
            value={draft}
            dir={dir}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); }
                if (e.key === "Escape") { setDraft(value); setEditing(false); }
            }}
            onClick={(e) => e.stopPropagation()}
            className="h-7 max-w-[260px]"
        />
    );
}

/* ------------------------------------------------------------------ */
/*  Page                                                              */
/* ------------------------------------------------------------------ */

export default function CategoriesPage() {
    const { t, lang } = useI18n();
    const { user } = useAuth();
    const isSuperAdmin = user?.role === ROLES.SUPER_ADMIN;

    const [tree, setTree] = useState(null);
    const [usage, setUsage] = useState({});
    const [expanded, setExpanded] = useState(loadExpandedState);
    const [selected, setSelected] = useState(() => new Set());
    const [query, setQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [tagFilter, setTagFilter] = useState("");

    const [dialog, setDialog] = useState({ open: false, mode: "create", initial: null });
    const [confirmDel, setConfirmDel] = useState(null);
    const [deleteForce, setDeleteForce] = useState(false);
    const [drag, setDrag] = useState(null); // { id, sourceParents, level }

    const debounced = useDebounce(query, 250);

    useEffect(() => { saveExpandedState(expanded); }, [expanded]);

    const refresh = useCallback(async () => {
        try {
            const [items, u] = await Promise.all([
                categoriesApi.listTree(false),
                categoriesApi.usage().catch(() => ({})),
            ]);
            setTree(items);
            setUsage(u || {});
            setExpanded((prev) => {
                if (prev.size > 0) return prev;
                const next = new Set();
                if (items[0]) next.add(items[0].id);
                return next;
            });
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
        }
    }, []);

    useEffect(() => { refresh(); }, [refresh]);

    const l1List = useMemo(
        () => (tree || []).map((n) => ({ id: n.id, name_en: n.name_en, name_ar: n.name_ar })),
        [tree],
    );

    /* ---- Search auto-expand: any L1 with a matching descendant or own match ---- */
    const matchesQuery = useCallback((node, q) => {
        if (!q) return true;
        const hay = [
            node.name_en, node.name_ar, node.explanatory_name_ar,
            ...(node.tags_en || []), ...(node.tags_ar || []),
        ].filter(Boolean).join(" ").toLowerCase();
        return hay.includes(q);
    }, []);

    /* ---- Build flat row list for the table ---- */
    const flatRows = useMemo(() => {
        if (!tree) return [];
        const q = debounced.trim().toLowerCase();
        const rows = [];
        for (const l1 of tree) {
            const l1Match = matchesQuery(l1, q);
            const childMatches = (l1.children || []).filter((c) => matchesQuery(c, q));
            if (q && !l1Match && childMatches.length === 0) continue;
            if (statusFilter === "active" && !l1.is_active) continue;
            if (statusFilter === "inactive" && l1.is_active) continue;
            if (tagFilter === "untagged" && (l1.tags_en?.length || l1.tags_ar?.length)) continue;
            if (tagFilter === "tagged" && !(l1.tags_en?.length || l1.tags_ar?.length)) {
                // L1 with no own tags is allowed — fallthrough; we filter visibly only
            }
            rows.push({ ...l1, level: 1 });
            const isExpanded = q ? true : expanded.has(l1.id);
            if (isExpanded) {
                const list = q ? childMatches : (l1.children || []);
                for (const c of list) {
                    if (statusFilter === "active" && !c.is_active) continue;
                    if (statusFilter === "inactive" && c.is_active) continue;
                    if (tagFilter === "untagged" && (c.tags_en?.length || c.tags_ar?.length)) continue;
                    if (tagFilter === "tagged" && !(c.tags_en?.length || c.tags_ar?.length)) continue;
                    // Poly-hierarchy subcategories appear under each of their
                    // expanded parents. The composite React key below keeps
                    // duplicate rows tracked separately so they all unmount
                    // cleanly when the parent collapses.
                    rows.push({ ...c, level: 2, parent_l1: l1.id });
                }
            }
        }
        return rows;
    }, [tree, expanded, debounced, statusFilter, tagFilter, matchesQuery]);

    const toggle = (id) => setExpanded((prev) => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
    });
    const expandAll = () => setExpanded(new Set((tree || []).map((n) => n.id)));
    const collapseAll = () => setExpanded(new Set());

    const toggleSel = (id) => setSelected((prev) => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
    });
    const clearSel = () => setSelected(new Set());

    /* ---- Inline rename commit ---- */
    const renameField = async (node, field, value) => {
        const updated = await categoriesApi.update(node.id, { [field]: value });
        // Patch tree in place
        setTree((prev) => prev.map((l1) => {
            if (l1.id === node.id) return { ...l1, ...updated };
            return {
                ...l1,
                children: (l1.children || []).map((c) => c.id === node.id ? { ...c, ...updated } : c),
            };
        }));
    };

    /* ---- Bulk activate/deactivate/delete ---- */
    const setActiveBulk = async (active) => {
        const ids = Array.from(selected);
        if (ids.length === 0) return;
        try {
            await Promise.all(ids.map((id) => categoriesApi.update(id, { is_active: active })));
            toast.success(active ? t("categories.toasts.activated", { n: ids.length }) : t("categories.toasts.deactivated", { n: ids.length }));
            clearSel();
            refresh();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
        }
    };
    const deleteBulk = async () => {
        const ids = Array.from(selected);
        if (ids.length === 0) return;
        if (!window.confirm(t("categories.confirmBulkDelete", { n: ids.length }))) return;
        try {
            await Promise.all(ids.map((id) => categoriesApi.remove(id, { force: true })));
            toast.success(t("categories.toasts.deletedN", { n: ids.length }));
            clearSel();
            refresh();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
        }
    };

    /* ---- Drag & drop reorder + Alt-drag re-parent ---- */
    const onDragStart = (row, e) => {
        setDrag({ id: row.id, level: row.level, parent_l1: row.parent_l1, parent_ids: row.parent_ids || [] });
        e.dataTransfer.effectAllowed = "move";
        try { e.dataTransfer.setData("text/plain", row.id); } catch (_) {}
    };
    const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };
    const onDropOnRow = async (target, e) => {
        e.preventDefault();
        if (!drag || drag.id === target.id) { setDrag(null); return; }
        try {
            // Alt-drag → re-parent (only valid on L2 → L1 row)
            if (e.altKey && drag.level === 2 && target.level === 1) {
                const parents = drag.parent_ids.includes(target.id)
                    ? drag.parent_ids.filter((p) => p !== target.id)  // toggle off
                    : [...drag.parent_ids, target.id];                 // add
                if (parents.length === 0) {
                    toast.error(t("categories.errors.parentRequired"));
                    return;
                }
                await categoriesApi.update(drag.id, { parent_ids: parents });
                toast.success(t("categories.toasts.reparented"));
                refresh();
                return;
            }
            // Same-level reorder
            if (drag.level !== target.level) return;
            if (drag.level === 2 && drag.parent_l1 !== target.parent_l1) return; // siblings only
            // Compute new sort_order = target's order, shift others (simple swap-based approach)
            const list = drag.level === 1
                ? (tree || [])
                : (tree.find((l) => l.id === drag.parent_l1)?.children || []);
            const ids = list.map((n) => n.id);
            const fromIdx = ids.indexOf(drag.id);
            const toIdx = ids.indexOf(target.id);
            if (fromIdx === -1 || toIdx === -1) return;
            const reordered = [...ids];
            reordered.splice(fromIdx, 1);
            reordered.splice(toIdx, 0, drag.id);
            // Persist new sort_order = position * 10 (gap for future inserts)
            await Promise.all(reordered.map((id, idx) => categoriesApi.update(id, { sort_order: (idx + 1) * 10 })));
            toast.success(t("categories.toasts.reordered"));
            refresh();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
        } finally {
            setDrag(null);
        }
    };

    /* ---- Delete with usage warning ---- */
    const performDelete = async () => {
        if (!confirmDel) return;
        try {
            await categoriesApi.remove(confirmDel.id, { force: deleteForce });
            toast.success(t("categories.toasts.deleted"));
            setConfirmDel(null);
            setDeleteForce(false);
            refresh();
        } catch (err) {
            const msg = formatApiErrorDetail(err?.response?.data?.detail) || err.message;
            // If the backend says "Pass ?force=true", show the force option
            if (msg.includes("force=true") || (err?.response?.status === 409)) {
                setDeleteForce(true);
                toast.warning(msg);
            } else {
                toast.error(msg);
            }
        }
    };

    /* ---- UI ---- */
    return (
        <div className="max-w-7xl space-y-4 fade-up" data-testid="page-admin-categories">
            <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">{t("categories.title")}</h1>
                    <p className="mt-2 text-sm text-muted-foreground max-w-2xl">{t("categories.subtitle")}</p>
                </div>
                {isSuperAdmin && (
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={expandAll}>{t("categories.expandAll")}</Button>
                        <Button variant="outline" onClick={collapseAll}>{t("categories.collapseAll")}</Button>
                        <Button onClick={() => setDialog({ open: true, mode: "create", initial: { level: 1 } })} data-testid="category-add-l1">
                            <Plus className="h-4 w-4 me-1" />{t("categories.addL1")}
                        </Button>
                    </div>
                )}
            </header>

            {/* Toolbar */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                <div className="relative flex-1">
                    <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                    <Input
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder={t("categories.searchPlaceholder")}
                        className="ps-10"
                        data-testid="category-search-input"
                    />
                </div>
                <Popover>
                    <PopoverTrigger asChild>
                        <Button variant="outline" data-testid="categories-filters">
                            <SlidersHorizontal className="h-4 w-4 me-2" />
                            {t("table.filters")}
                            {(statusFilter || tagFilter) && (
                                <span className="ms-2 inline-flex items-center justify-center h-5 min-w-5 px-1.5 rounded-full bg-primary text-primary-foreground text-[10px] font-semibold">
                                    {(statusFilter ? 1 : 0) + (tagFilter ? 1 : 0)}
                                </span>
                            )}
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-72" align="end">
                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                                <p className="text-sm font-semibold">{t("table.filters")}</p>
                                {(statusFilter || tagFilter) && (
                                    <button type="button" onClick={() => { setStatusFilter(""); setTagFilter(""); }}
                                            className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center">
                                        <X className="h-3 w-3 me-1" />{t("table.clearAll")}
                                    </button>
                                )}
                            </div>
                            <div>
                                <Label className="text-xs text-muted-foreground">{t("categories.fields.active")}</Label>
                                <Select value={statusFilter || "__all__"} onValueChange={(v) => setStatusFilter(v === "__all__" ? "" : v)}>
                                    <SelectTrigger className="mt-1"><SelectValue placeholder={t("table.allValues")} /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="__all__">{t("table.allValues")}</SelectItem>
                                        <SelectItem value="active">{t("categories.statusActive")}</SelectItem>
                                        <SelectItem value="inactive">{t("categories.statusInactive")}</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-xs text-muted-foreground">{t("categories.tagsFilter")}</Label>
                                <Select value={tagFilter || "__all__"} onValueChange={(v) => setTagFilter(v === "__all__" ? "" : v)}>
                                    <SelectTrigger className="mt-1"><SelectValue placeholder={t("table.allValues")} /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="__all__">{t("table.allValues")}</SelectItem>
                                        <SelectItem value="tagged">{t("categories.tagged")}</SelectItem>
                                        <SelectItem value="untagged">{t("categories.untagged")}</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </PopoverContent>
                </Popover>
            </div>

            {/* Drag hint banner */}
            {isSuperAdmin && (
                <div className="rounded-md border border-primary/20 bg-primary/5 px-3 py-1.5 text-xs text-primary inline-flex items-center gap-2">
                    <GripVertical className="h-3.5 w-3.5" />
                    {t("categories.dragHint")}
                </div>
            )}

            {/* Bulk action bar */}
            {selected.size > 0 && (
                <div className="rounded-lg border border-primary/30 bg-primary/5 px-4 py-2 flex items-center justify-between" data-testid="categories-bulk-bar">
                    <p className="text-sm font-medium">{t("categories.nSelected", { n: selected.size })}</p>
                    <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => setActiveBulk(true)}>
                            <Eye className="h-3.5 w-3.5 me-1" />{t("categories.activate")}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setActiveBulk(false)}>
                            <EyeOff className="h-3.5 w-3.5 me-1" />{t("categories.deactivate")}
                        </Button>
                        <Button size="sm" variant="outline" onClick={deleteBulk} className="text-destructive hover:text-destructive">
                            <Trash2 className="h-3.5 w-3.5 me-1" />{t("common.delete")}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={clearSel}><X className="h-3.5 w-3.5" /></Button>
                    </div>
                </div>
            )}

            {/* Table */}
            {tree === null ? (
                <div className="surface-card p-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
            ) : tree.length === 0 ? (
                <div className="surface-card p-10 text-center">
                    <Sparkles className="mx-auto h-8 w-8 text-primary mb-3" />
                    <p className="text-base font-medium">{t("categories.empty")}</p>
                    <p className="mt-1 text-sm text-muted-foreground">{t("categories.emptyHint")}</p>
                </div>
            ) : (
                <div className="surface-card overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="categories-table">
                            <thead className="bg-muted/30">
                                <tr>
                                    {isSuperAdmin && <th className="w-8" />}
                                    <th className="w-8" />
                                    <th className="text-start px-3 py-3 font-semibold">{t("categories.cols.name")}</th>
                                    <th className="text-start px-3 py-3 font-semibold">{t("categories.cols.translation")}</th>
                                    <th className="text-start px-3 py-3 font-semibold">{t("categories.cols.providers")}</th>
                                    <th className="text-start px-3 py-3 font-semibold">{t("categories.cols.offers")}</th>
                                    <th className="text-start px-3 py-3 font-semibold">{t("categories.cols.tags")}</th>
                                    <th className="text-start px-3 py-3 font-semibold">{t("categories.cols.updated")}</th>
                                    {isSuperAdmin && <th className="px-3 py-3" />}
                                </tr>
                            </thead>
                            <tbody>
                                {flatRows.length === 0 && (
                                    <tr><td colSpan={9} className="px-3 py-8 text-center text-sm text-muted-foreground">
                                        {debounced ? t("categories.noResults") : t("categories.empty")}
                                    </td></tr>
                                )}
                                {flatRows.map((r) => {
                                    const isL1 = r.level === 1;
                                    const u = usage[r.id] || { providers: 0, offers: 0 };
                                    const isExpanded = expanded.has(r.id);
                                    const beingDragged = drag?.id === r.id;
                                    const isAltTarget = drag && drag.level === 2 && r.level === 1 && drag.id !== r.id;
                                    const tagsCount = (r.tags_en?.length || 0) + (r.tags_ar?.length || 0);
                                    const translationComplete = !!r.name_en && !!r.name_ar;
                                    return (
                                        <tr
                                            key={r.parent_l1 ? `${r.parent_l1}-${r.id}` : r.id}
                                            draggable={isSuperAdmin}
                                            onDragStart={(e) => onDragStart(r, e)}
                                            onDragOver={isSuperAdmin ? onDragOver : undefined}
                                            onDrop={isSuperAdmin ? (e) => onDropOnRow(r, e) : undefined}
                                            onDragEnd={() => setDrag(null)}
                                            className={[
                                                "border-t border-border group",
                                                beingDragged ? "opacity-50 bg-primary/5" : "hover:bg-muted/20",
                                                isAltTarget ? "outline outline-1 outline-primary/30" : "",
                                            ].join(" ")}
                                            data-testid={`category-row-${r.id}`}
                                        >
                                            {isSuperAdmin && (
                                                <td className="ps-2 py-2 text-center">
                                                    <GripVertical className="h-4 w-4 text-muted-foreground/40 group-hover:text-muted-foreground cursor-grab inline" />
                                                </td>
                                            )}
                                            <td className="py-2 text-center" onClick={(e) => e.stopPropagation()}>
                                                <button type="button" onClick={() => toggleSel(r.id)} aria-label="Select">
                                                    {selected.has(r.id)
                                                        ? <CheckSquare className="h-4 w-4 text-primary inline" />
                                                        : <Square className="h-4 w-4 text-muted-foreground/50 hover:text-foreground inline" />}
                                                </button>
                                            </td>
                                            <td className="px-3 py-2">
                                                <div className="flex items-center gap-2" style={{ paddingInlineStart: r.level === 2 ? "20px" : 0 }}>
                                                    {isL1 ? (
                                                        <button type="button" onClick={() => toggle(r.id)} className="text-muted-foreground hover:text-foreground" aria-label={isExpanded ? "Collapse" : "Expand"}>
                                                            {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                                        </button>
                                                    ) : (
                                                        <span className="w-4 inline-block" />
                                                    )}
                                                    <StatusDot active={r.is_active} />
                                                    <InlineName
                                                        value={lang === "ar" ? r.name_ar : r.name_en}
                                                        lang={lang}
                                                        isL1={isL1}
                                                        dir={lang === "ar" ? "rtl" : "ltr"}
                                                        onCommit={(v) => renameField(r, lang === "ar" ? "name_ar" : "name_en", v)}
                                                    />
                                                    {!isL1 && (r.parent_ids?.length || 0) > 1 && (
                                                        <span className="inline-flex flex-wrap gap-1">
                                                            {r.parent_ids.map((pid) => {
                                                                const parent = tree.find((l) => l.id === pid);
                                                                if (!parent) return null;
                                                                return (
                                                                    <span key={pid} className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary font-medium">
                                                                        {lang === "ar" ? parent.name_ar : parent.name_en}
                                                                    </span>
                                                                );
                                                            })}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-3 py-2 text-xs">
                                                {translationComplete ? (
                                                    <span className="text-muted-foreground">EN · AR</span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 text-amber-700">
                                                        <Languages className="h-3 w-3" />
                                                        {!r.name_en ? "EN missing" : "AR missing"}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-3 py-2">
                                                <span className="inline-flex items-center gap-1">
                                                    <Building2 className="h-3 w-3 text-muted-foreground" />
                                                    {u.providers}
                                                </span>
                                            </td>
                                            <td className="px-3 py-2">
                                                <span className="inline-flex items-center gap-1">
                                                    <Sparkles className="h-3 w-3 text-muted-foreground" />
                                                    {u.offers}
                                                </span>
                                            </td>
                                            <td className="px-3 py-2">
                                                {tagsCount > 0 ? (
                                                    <div className="inline-flex flex-wrap gap-1">
                                                        {(r.tags_en || []).slice(0, 2).map((tg) => (
                                                            <span key={`en-${tg}`} className="text-[10px] px-1.5 py-0.5 rounded bg-secondary">{tg}</span>
                                                        ))}
                                                        {tagsCount > 2 && (
                                                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">+{tagsCount - 2}</span>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
                                                        <Tag className="h-3 w-3" />—
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">
                                                {fmtDate(r.updated_at, lang)}
                                            </td>
                                            {isSuperAdmin && (
                                                <td className="px-3 py-2 text-end">
                                                    <div className="opacity-0 group-hover:opacity-100 transition-opacity inline-flex">
                                                        {isL1 && (
                                                            <Button
                                                                size="icon" variant="ghost" className="h-7 w-7"
                                                                onClick={(e) => { e.stopPropagation(); setDialog({ open: true, mode: "create", initial: { level: 2, parent_ids: [r.id] } }); }}
                                                                title={t("categories.addL2")}
                                                                data-testid={`category-add-l2-${r.id}`}
                                                            >
                                                                <Plus className="h-3.5 w-3.5" />
                                                            </Button>
                                                        )}
                                                        <Button
                                                            size="icon" variant="ghost" className="h-7 w-7"
                                                            onClick={(e) => { e.stopPropagation(); setDialog({ open: true, mode: "edit", initial: r }); }}
                                                            title={t("common.edit")}
                                                            data-testid={`category-edit-${r.id}`}
                                                        >
                                                            <Pencil className="h-3.5 w-3.5" />
                                                        </Button>
                                                        <Button
                                                            size="icon" variant="ghost" className="h-7 w-7"
                                                            onClick={(e) => { e.stopPropagation(); setConfirmDel(r); setDeleteForce(false); }}
                                                            title={t("common.delete")}
                                                            data-testid={`category-delete-${r.id}`}
                                                        >
                                                            <Trash2 className="h-3.5 w-3.5 text-destructive" />
                                                        </Button>
                                                    </div>
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Footer summary */}
            {tree && tree.length > 0 && (
                <p className="text-xs text-muted-foreground text-center">
                    {t("categories.summary", {
                        l1: tree.length,
                        l2: tree.reduce((acc, l) => acc + (l.children?.length || 0), 0),
                        inactive: tree.reduce((acc, l) => acc + (l.is_active ? 0 : 1) + (l.children || []).filter((c) => !c.is_active).length, 0),
                    })}
                </p>
            )}

            <CategoryEditPanel
                open={dialog.open}
                onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))}
                mode={dialog.mode}
                initial={dialog.initial}
                tree={tree}
                usage={usage}
                onSaved={(saved, opts) => {
                    refresh();
                    // If the user picked "Save and add another" we keep the panel open with reset state.
                    // Panel handles its own reset; we just refresh data here.
                    if (opts?.intent === "save_add_l2" && saved?.id) {
                        // Switch the dialog state so the page knows we're now editing under a new parent
                        setDialog({ open: true, mode: "create", initial: { level: 2, parent_ids: [saved.id] } });
                    }
                }}
            />

            <AlertDialog open={!!confirmDel} onOpenChange={(o) => { if (!o) { setConfirmDel(null); setDeleteForce(false); } }}>
                <AlertDialogContent data-testid="category-delete-confirm">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t("categories.confirmDeleteTitle")}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t("categories.confirmDeleteBody", { name: confirmDel?.name_en || "" })}
                            {confirmDel && (() => {
                                const u = usage[confirmDel.id] || { providers: 0, offers: 0 };
                                if (u.providers > 0 || u.offers > 0) {
                                    return (
                                        <span className="block mt-2 rounded-lg border border-warning/40 bg-warning/5 p-2 text-xs text-foreground">
                                            {t("categories.deleteImpact", { providers: u.providers, offers: u.offers })}
                                        </span>
                                    );
                                }
                                return null;
                            })()}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={performDelete}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            data-testid="category-delete-confirm-button"
                        >
                            {deleteForce ? t("categories.forceDelete") : t("categories.confirmDeleteAction")}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
